"use client";

import { forwardRef, memo } from "react";
import { FlaskConical, XIcon } from "lucide-react";

import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import { TabsTrigger } from "@acme/ui/tabs";

import { getExtensionIcon } from "../../_utils/languages";

interface FileTabProps {
  value: string;
  onRemove: () => void;
  onContextMenu?: () => void;
  name: string;
  disabledRemove?: boolean;
  type: "test" | "file";
}

export const FileTab = memo(
  forwardRef<HTMLDivElement, FileTabProps>(function FileTab(
    { value, onRemove, onContextMenu, name, disabledRemove, type },
    ref,
  ) {
    return (
      <TabsTrigger
        value={value}
        data-umami-event="studio:select-remote-run-file"
        className={cn(
          "group p-0 select-none data-[state=active]:z-20 data-[state=active]:bg-inherit! data-[state=active]:shadow-none!",
          "active:cursor-grabbing",
        )}
        asChild
      >
        <div
          className="relative z-1 h-[36px]!"
          ref={ref}
          onContextMenu={() => onContextMenu?.()}
          onAuxClick={(e) => {
            if (e.button === 1 && !disabledRemove) {
              e.preventDefault();
              onRemove();
            }
          }}
        >
          <div className="pointer-events-none absolute top-px bottom-0 left-0 w-full opacity-0 transition-opacity duration-200 group-hover:opacity-50 group-data-[state=active]:opacity-100">
            <svg
              className="h-full w-full"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <svg width="52%" height="100%">
                <use
                  xlinkHref="#tab-geometry-left"
                  width={214}
                  height={36}
                  className="fill-muted"
                />
              </svg>
              <g transform="scale(-1, 1)">
                <svg width="52%" height="100%" x="-100%" y={0}>
                  <use
                    xlinkHref="#tab-geometry-right"
                    width={214}
                    height={36}
                    className="fill-muted"
                  />
                </svg>
              </g>
            </svg>
          </div>
          <div className="relative flex max-w-[250px] items-center overflow-hidden rounded-t-lg pr-4 pl-5">
            {type === "test" && <FlaskConical className="size-3" />}

            {type === "file" && (
              <i className="size-4">{getExtensionIcon(name).icon}</i>
            )}

            <div className="mr-2 ml-1.5 flex-1 overflow-hidden align-top font-mono text-sm whitespace-nowrap">
              {name}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "group-data[state=active]:block hover:bg-foreground/25 z-10 size-5 p-1 opacity-50 group-hover:opacity-100",
                disabledRemove && "opacity-30!",
                "shrink-0 grow-0",
              )}
              onClick={onRemove}
              disabled={disabledRemove}
            >
              <XIcon className="size-4" />
            </Button>
          </div>
        </div>
      </TabsTrigger>
    );
  }),
);
